<template>
    <v-container style="max-width: 1240px; padding-left: 0px; padding-right: 0px;">
        <v-layout row wrap mb-2>
            <v-flex xs12 sm6 md3 py0 class="sm4">
                <v-autocomplete
                    label="В каком банке ипотека"
                    :items="banks"
                    item-text="name"
                    item-value="alias"
                    v-model="value.bank"
                    :rules="[rules.required]"
                    @change="change"
                    :search-input.sync="filters.companySearch"
                    :allow-overflow=false
                >
                    <template v-slot:no-data>
                        <v-list-tile>
                            <v-list-tile-content>
                                <v-list-tile-title>
                                    Не найдено: "<strong>{{ filters.companySearch }}</strong>".
                                </v-list-tile-title>
                            </v-list-tile-content>
                        </v-list-tile>
                    </template>
                </v-autocomplete>
            </v-flex>
            <v-flex xs12 sm6 md3>
                <v-text-field
                    label="Остаток кредита"
                    v-model.number="value.creditRemainder"
                    :rules="[rules.required, rules.remainderMin, rules.remainderMax, rules.float]"
                    min="100000"
                    max="25000000"
                    v-on:input="value.creditRemainder = $event.replace(/,/, '.')"
                    v-on:focus="setFocus"
                    v-on:change="setFormat"
                />
            </v-flex>
            <v-flex xs12 sm6 md3>
                <v-select
                    label="Пол заемщика"
                    :items="genders"
                    item-text="key"
                    item-value="val"
                    v-model="value.gender"
                    :rules="[rules.required]"
                    :menu-props="{ bottom: true, offsetY: true }"
                />
            </v-flex>
            <v-flex xs12 sm6 md3>
                <osago-date
                    v-model="value.birthday"
                    label="Дата рождения"
                    isBirthday="true"
                    :max="birthdayMinimal"
                    :isHideMobile="true"
                    :rules="[rules.required,rules.birthday]"
                />
            </v-flex>
            <v-flex xs12 sm6 md3 v-if="value.bank === 'vtb' && showContractDate">
                <osago-date
                    v-model="value.contract.dateStart"
                    label="Дата начала кредита"
                    :yearStarting="false"
                    min="2000-12-31"
                    :rules="[rules.required]"
                />
            </v-flex>
        </v-layout>
        <v-layout row wrap mb-2>
            <v-flex xs12>
                <h3>Выбор типа полиса</h3>
            </v-flex>
            <v-flex xs12 sm2 py-0 mr-4>
                <v-checkbox
                    label="Квартира"
                    v-model="value.policyType.apartment"
                    @change="checkTitle(); validateCheckboxes(); policyTypeChanged()"
                    :error="!isAtLeastOneSelected"
                    :rules="[isAtLeastOneSelected || 'Выберите тип полиса']"
                />
            </v-flex>
            <v-flex xs12 sm2 py-0>
                <v-checkbox
                    label="Жизнь"
                    v-model="value.policyType.life"
                    @change="validateCheckboxes(); policyTypeChanged()"
                    :error="!isAtLeastOneSelected"
                />
            </v-flex>
            <v-flex xs12 sm2 py-0>
                <v-checkbox
                    label="Титул"
                    v-model="value.policyType.title"
                    @change="checkApartment(); validateCheckboxes()"
                />
            </v-flex>
        </v-layout>
        <v-layout row wrap v-if="step > 1 && value.policyType.apartment">
            <v-flex xs12>
                <h3>Дополнительные сведения</h3>
            </v-flex>
            <v-flex xs12 sm4 py-0>
                <v-checkbox
                    label="Есть деревянные перекрытия"
                    v-model="value.additionalProperties.hasWoodenFloors"
                    @change="changeCheckboxData"
                />
            </v-flex>
            <v-flex xs12 sm4 py-0>
                <v-checkbox
                    label="Дом газифицирован"
                    v-model="value.additionalProperties.houseGasificated"
                    @change="changeCheckboxData"
                />
            </v-flex>
        </v-layout>
        <v-layout row wrap v-if="step > 1 && value.policyType.title">
            <v-flex xs12>
                <h3>Требования к сделке по недвижимости</h3>
            </v-flex>
            <v-layout flex row wrap class="requirements">
                <v-flex lg6 md6 sm6 xs12>
                    <p>Отсутствуют собственники младше 18 и старше 65 лет</p>
                </v-flex>
                <v-flex lg6 md6 sm6 xs12>
                    <p>Недвижимость была в собственности более 3-х лет</p>
                </v-flex>
                <v-flex lg6 md6 sm6 xs12>
                    <p>Сделка заключена не по доверенности</p>
                </v-flex>
                <v-flex lg6 md6 sm6 xs12>
                    <p>Среди собственников отсутствуют юр. лица</p>
                </v-flex>
                <v-flex lg6 md6 sm6 xs12>
                    <p>Есть согласие супруга/супруги на заключение сделки</p>
                </v-flex>
                <v-flex lg6 md6 sm6 xs12>
                    <p>Ограничения права собственности отсутствуют</p>
                </v-flex>
                <v-flex lg6 md6 sm6 xs12>
                    <p>Документ подтверждающий право собственности - договор купли продажи</p>
                </v-flex>
            </v-layout>
            <v-checkbox
                class="requirements-accept"
                label="Я подтверждаю, что сделка соответствует вышеуказанным требованиям"
                v-model="value.acceptTitleRules"
                :rules=[rules.title]
                @change="changeCheckboxData"
            />
        </v-layout>
    </v-container>
</template>

<script>
import moment from "moment";

export default {
    name: "MortgageCalc",
    props: {
        value: {
            default: {
                bank: null,
                banks: null,
                creditRemainder: null,
                gender: null,
                birthday: null,
                additionalProperties: {
                    hasWoodenFloors: false,
                    houseGasificated: false
                },
                policyType: {
                    life: true,
                    apartment: true,
                    title: false,
                },
                acceptTitleRules: false,
            }
        },
        step: {
            default: 1
        },
        showContractDate: {
            type: Boolean,
            default: true
        }
    },
    data: () => ({
        banks: [],
        genders: [{key: 'Мужской', val: 'm'}, {key: 'Женский', val: 'f'}],
        birthdayMinimal: null,

        rules: {
            required: v => !!v || 'Поле обязательно для заполнения',
            digits: v => Number.isInteger(v) || 'Значение должно быть целым числом',
            remainderMin: v => parseInt((v) ? v.replace(/\s/g, '') : v) >= 100000 || 'Сумма не может быть меньше 100 000',
            remainderMax: v => parseInt((v) ? v.replace(/\s/g, '') : v) <= 25000000 || 'Сумма не может быть больше 25 000 000',
            birthday: v => this.validBirthday(v),
            float: v => !isNaN(parseFloat(v)) || 'Значение должно быть дробным или целым числом',
            title: v => v || 'Для оформления титула необходимо согласиться с условиями',
        },
        filters: {
            companySearch: '',
        },
        isAtLeastOneSelected: false,
    }),
    watch: {
        'value.birthday' : {
            handler: function(val, oldVal) {
                this.changeBirthday(val)
            },
            deep: true
        }
    },
    created() {
        this.banks = this._props.value.banks.filter(item => item.active);
        this.birthdayMinimal = moment().subtract(18, 'year').format('YYYY-MM-DD');
        this.validateCheckboxes();
    },
    methods: {
        setFocus() {
            if (this.value.creditRemainder) this.value.creditRemainder = this.value.creditRemainder.toString().replace(/\s/g, '')
        },
        setFormat(value) {
            let sum = value.replace(/\s/g, '')
            sum = Math.floor(Math.round(parseFloat(sum) * 100)) / 100
            this.value.creditRemainder = sum.toString().replace(/(\d)(?=(\d{3})+([^\d]|$))/g, '$1 ')
            this.countFloatSymbols(this.value.creditRemainder) === 1 ? this.value.creditRemainder += '0' : {}
        },
        countFloatSymbols(x) {
            return x.includes('.') ? x.split('.').pop().length : 0
        },
        changeBirthday(value) {
            this.$emit('changeBirthday', value)
        },
        validBirthday(date) {
            const m = moment(date, 'DD.MM.YYYY');
            const isValid = m.format('DD.MM.YYYY') === date;
            if (!isValid) return 'Неверный формат даты';

            const diff = moment().diff(m, 'years');
            if (diff < 18) return 'Меньше 18 лет';
            if (diff > 75) return 'Больше 75 лет';

            return true;
        },
        change() {
            this.$emit('changedBank', this.value)
        },
        changeCheckboxData() {
            if(this.step > 1) {
                this.$emit('save')
            }
        },
        validateCheckboxes() {
            this.isAtLeastOneSelected = this.value.policyType.apartment || this.value.policyType.life;
        },
        checkApartment() {
            if (this.value.policyType.title) {
                this.$emit('checkApartment')
            }
        },
        checkTitle() {
            if (this.value.policyType.apartment === false) {
                this.$emit('checkTitle')
            }
        },
        policyTypeChanged() {
            this.$emit('policyTypeChanged');
        }
    },
    computed: {
        formatted() {
            return this.values.map(n => n.toString().replace(/(\d)(?=(\d{3})+([^\d]|$))/g, '$1 '));
        },
    },
}
</script>
<style>
</style>



// WEBPACK FOOTER //
// resources/js/components/mortgage/MortgagePrecalcInfo.vue